import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { Gig, PerformerStatus } from '@stage/contracts';
import { publicEventUrl } from '@stage/config';
import { ApiError, type AdminInteraction } from '../api/client.js';
import { useAdminClient } from '../hooks/useAdminClient.js';
import { useInteractionQueue } from '../hooks/useInteractionQueue.js';
import { QueueCard } from '../components/QueueCard.js';
import { SetlistConsoleView } from '../components/SetlistConsoleView.js';
import { BriefConsoleView } from '../components/BriefConsoleView.js';
import { useOnline } from '../hooks/useOnline.js';
import { useWakeLock } from '../hooks/useWakeLock.js';
import { lifecycle, statusLabel } from '../lib/lifecycle.js';

/**
 * Performance Console (§24). Dark, large-target stage layout with live-state controls (§24.2) and an
 * interaction queue that polls every 2s while visible and live (§24.4). Local state-changing actions
 * show a pending indicator; the console never implies the audience saw a local-only change (§24.5).
 */

type Filter =
  | 'new'
  | 'shortlisted'
  | 'queued'
  | 'song_request'
  | 'freeform'
  | 'performed'
  | 'hidden';

const FILTERS: ReadonlyArray<{ key: Filter; label: string }> = [
  { key: 'new', label: 'New' },
  { key: 'shortlisted', label: 'Shortlisted' },
  { key: 'queued', label: 'Queued' },
  { key: 'song_request', label: 'Song requests' },
  { key: 'freeform', label: 'Free-form' },
  { key: 'performed', label: 'Performed' },
  { key: 'hidden', label: 'Hidden' },
];

function matches(filter: Filter, it: AdminInteraction): boolean {
  switch (filter) {
    case 'song_request':
    case 'freeform':
      return it.interactionType === filter;
    default:
      return it.performerStatus === filter;
  }
}

function freshnessLabel(lastUpdatedAt: number | null, now: number): string {
  if (!lastUpdatedAt) return 'never';
  const secs = Math.max(0, Math.round((now - lastUpdatedAt) / 1000));
  if (secs < 2) return 'just now';
  return `${secs}s ago`;
}

export function PerformanceConsolePage() {
  const { gigId = '' } = useParams();
  const client = useAdminClient();
  const [gig, setGig] = useState<Gig | null>(null);
  const [gigError, setGigError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [nowPlayingDraft, setNowPlayingDraft] = useState('');
  const [pendingIds, setPendingIds] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState<Filter>('new');
  const [now, setNow] = useState(Date.now());

  const active = gig ? lifecycle.isRunning(gig.status) : false;
  const queue = useInteractionQueue(gigId, active);
  const online = useOnline();
  const wakeLock = useWakeLock();

  const loadGig = useCallback(async () => {
    setGigError(null);
    try {
      setGig(await client.getGig(gigId));
    } catch (err) {
      setGigError(err instanceof ApiError ? err.message : 'Could not load this gig.');
    }
  }, [client, gigId]);

  useEffect(() => {
    void loadGig();
  }, [loadGig]);

  // Tick once a second to keep the freshness label current.
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const transition = useCallback(
    async (action: () => Promise<Gig>) => {
      setBusy(true);
      try {
        setGig(await action());
      } catch (err) {
        setGigError(err instanceof ApiError ? err.message : 'Action failed.');
      } finally {
        setBusy(false);
      }
    },
    [],
  );

  const setNowPlaying = useCallback(
    async (value: string | null) => {
      setBusy(true);
      try {
        setGig(await client.patchLiveState(gigId, { customNowPlaying: value }));
        setNowPlayingDraft('');
      } catch (err) {
        setGigError(err instanceof ApiError ? err.message : 'Could not update now-playing.');
      } finally {
        setBusy(false);
      }
    },
    [client, gigId],
  );

  const act = useCallback(
    async (interaction: AdminInteraction, next: PerformerStatus) => {
      setPendingIds((prev) => new Set(prev).add(interaction.interactionId));
      try {
        const updated = await client.patchInteraction(gigId, interaction.interactionId, {
          performerStatus: next,
          version: interaction.version,
        });
        queue.applyLocal(updated);
      } catch {
        // Leave the card as-is; the next poll reconciles with server state (§24.4).
      } finally {
        setPendingIds((prev) => {
          const nextSet = new Set(prev);
          nextSet.delete(interaction.interactionId);
          return nextSet;
        });
      }
    },
    [client, gigId, queue],
  );

  const visible = useMemo(
    () => queue.items.filter((it) => matches(filter, it)),
    [queue.items, filter],
  );

  const newCount = useMemo(
    () => queue.items.filter((it) => it.performerStatus === 'new').length,
    [queue.items],
  );

  if (gigError && !gig) {
    return (
      <main className="console">
        <p className="error">{gigError}</p>
        <Link className="link" to="/app">
          Back to overview
        </Link>
      </main>
    );
  }
  if (!gig) {
    return (
      <main className="console" aria-busy="true">
        <p>Loading console…</p>
      </main>
    );
  }

  const connLabel =
    queue.connectivity === 'online'
      ? 'Connected'
      : queue.connectivity === 'offline'
        ? 'Offline'
        : 'Reconnecting…';

  return (
    <main className="console" data-mode="dark">
      <header className="console__bar">
        <div>
          <p className="eyebrow">
            <Link className="link" to={`/app/gigs/${gig.gigId}`}>
              {gig.name}
            </Link>
          </p>
          <p className="status">
            <span className={`dot status-dot--${gig.status}`} />
            {statusLabel(gig.status)}
          </p>
        </div>
        <div className="console__health" aria-live="polite">
          <span className={`conn conn--${queue.connectivity}`}>{connLabel}</span>
          <span className="muted">updated {freshnessLabel(queue.lastUpdatedAt, now)}</span>
          {pendingIds.size > 0 && (
            <span className="pending-badge" title="Unsent local actions">
              {pendingIds.size} pending
            </span>
          )}
          {wakeLock.supported && (
            <button
              type="button"
              className={`wake-toggle ${wakeLock.active ? 'wake-toggle--on' : ''}`}
              aria-pressed={wakeLock.active}
              onClick={wakeLock.toggle}
            >
              {wakeLock.active ? 'Screen awake ✓' : 'Keep screen awake'}
            </button>
          )}
          {wakeLock.error && <span className="error">{wakeLock.error}</span>}
        </div>
      </header>

      <section className="console__live">
        <div className="live-controls">
          <button
            type="button"
            className="big"
            disabled={!lifecycle.canStart(gig.status) || busy}
            onClick={() => void transition(() => client.startGig(gigId))}
          >
            Start gig
          </button>
          <button
            type="button"
            className="big"
            disabled={!lifecycle.canIntermission(gig.status) || busy}
            onClick={() => void transition(() => client.intermission(gigId))}
          >
            Start break
          </button>
          <button
            type="button"
            className="big"
            disabled={!lifecycle.canResume(gig.status) || busy}
            onClick={() => void transition(() => client.resumeGig(gigId))}
          >
            Resume
          </button>
          <button
            type="button"
            className="big danger"
            disabled={!lifecycle.canEnd(gig.status) || busy}
            onClick={() => {
              if (window.confirm('End this gig? Audience interactions will be disabled.')) {
                void transition(() => client.endGig(gigId));
              }
            }}
          >
            End gig
          </button>
        </div>

        <form
          className="now-playing-set"
          onSubmit={(e) => {
            e.preventDefault();
            if (nowPlayingDraft.trim()) void setNowPlaying(nowPlayingDraft.trim());
          }}
        >
          <label>
            Custom now-playing
            <input
              value={nowPlayingDraft}
              onChange={(e) => setNowPlayingDraft(e.target.value)}
              placeholder="e.g. Fly Me to the Moon"
              maxLength={160}
            />
          </label>
          <div className="button-row">
            <button type="submit" disabled={busy || !nowPlayingDraft.trim()}>
              Set
            </button>
            <button type="button" className="ghost" disabled={busy} onClick={() => void setNowPlaying(null)}>
              Clear
            </button>
          </div>
        </form>

        <p className="console__preview">
          <a href={publicEventUrl(gig.publicCode)} target="_blank" rel="noreferrer">
            Public-page preview ↗
          </a>
        </p>
        {gigError && <p className="error">{gigError}</p>}
        {!active && (
          <p className="hint">Queue polling pauses until the gig is live or in intermission.</p>
        )}
      </section>

      <SetlistConsoleView gigId={gigId} initialCurrentItemId={null} online={online} />

      <BriefConsoleView
        gigId={gigId}
        artistId={gig.artistId}
        active={active}
        online={online}
      />

      <section className="console__queue">
        <div className="filters" role="tablist" aria-label="Interaction filters">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              role="tab"
              aria-selected={filter === f.key}
              className={`filter ${filter === f.key ? 'filter--active' : ''}`}
              onClick={() => setFilter(f.key)}
            >
              {f.label}
              {f.key === 'new' && newCount > 0 && <span className="count">{newCount}</span>}
            </button>
          ))}
        </div>

        {visible.length === 0 ? (
          <p className="muted queue-empty">Nothing here yet.</p>
        ) : (
          <ul className="queue">
            {visible.map((it) => (
              <QueueCard
                key={it.interactionId}
                interaction={it}
                pending={pendingIds.has(it.interactionId)}
                onAct={(next) => void act(it, next)}
              />
            ))}
          </ul>
        )}
      </section>
    </main>
  );
}
